import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectConfig } from '../../redux/slices/configSlice';
import { Conversation } from '@twilio/conversations';

interface DOBInputProps {
  conversation?: Conversation;
  onSubmit?: (dob: string) => void;
}

const isValidDate = (day: string, month: string, year: string) => {
  const date = new Date(`${year}-${month}-${day}`);
  return (
    date.getDate() === parseInt(day) &&
    date.getMonth() + 1 === parseInt(month) &&
    date.getFullYear() === parseInt(year)
  );
};

const isInFuture = (day: string, month: string, year: string) => {
  const inputDate = new Date(`${year}-${month}-${day}`);
  const today = new Date();
  return inputDate > today;
};

const isTooOld = (year: string) => {
  return parseInt(year) < 1900;
};

const DOBInput: React.FC<DOBInputProps> = ({ conversation, onSubmit }) => {
  const config = useSelector(selectConfig);
  const RmConfigs = config?.data?.[0]?.attributes.RichMediaConfigs;
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // Check if screen width is below 425px
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 425);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 2) {
      setDay(value);
      if (value.length === 2) {
        monthRef.current?.focus();
      }
    }
    setError('');
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 2) {
      setMonth(value);
      if (value.length === 2) {
        yearRef.current?.focus();
      }
    }
    setError('');
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 4) setYear(value);
    setError('');
  };

  const validateDOB = () => {
    if (!day || !month || !year) {
      setError(RmConfigs?.RichmediaDOBIncompleteFieldErrorMessage || 'Please fill in all fields');
      return false;
    }

    if (parseInt(day) < 1 || parseInt(day) > 31) {
      setError(RmConfigs?.RichmediaDOBDayErrorMessage || 'Invalid day');
      return false;
    }

    if (parseInt(month) < 1 || parseInt(month) > 12) {
      setError(RmConfigs?.RichmediaDOBMonthErrorMessage || 'Invalid month');
      return false;
    }

    if (year.length !== 4) {
      setError(RmConfigs?.RichmediaDOBYearErrorMessage || 'Year must be 4 digits');
      return false;
    }

    if (!isValidDate(day, month, year)) {
      setError(RmConfigs?.RichmediaDOBInvalidDateErrorMessage || 'Invalid date');
      return false;
    }

    if (isInFuture(day, month, year)) {
      setError(RmConfigs?.RichmediaDOBFutureErrorMessage || 'Date cannot be in the future');
      return false;
    }

    if (isTooOld(year)) {
      setError(RmConfigs?.RichmediaDOBOldErrorMessage || 'Year cannot be before 1900');
      return false;
    }

    // Add 18-year-old validation
    const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();
    const ageDiff = today.getFullYear() - birthDate.getFullYear();
    const hasHadBirthdayThisYear =
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

    const age = hasHadBirthdayThisYear ? ageDiff : ageDiff - 1;

    if (age < 18) {
      setError(RmConfigs?.RichmediaDOBAgeErrorMessage || 'Must be 18 years or older');
      return false;
    }

    setError('');
    return true;
  };

  const handleBlur = () => {
    if (day && month && year) validateDOB();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateDOB()) return;

    const dob = `${month}/${day}/${year}`;
    setIsSubmitted(true);
    console.log('Submitted DOB:', dob);

    // Send DOB as message using conversation.sendMessage
    if (conversation) {
      try {
        await conversation.sendMessage(dob);
        console.log('✅ DOB sent successfully:', dob);
        onSubmit?.(dob);
      } catch (error) {
        console.error('❌ Failed to send DOB:', error);
        setIsSubmitted(false);
        return;
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: isMobile ? '100%' : '300px',
        textAlign: 'left',
        background: '#e5e5ea',
        padding: isMobile ? '16px' : '12px',
        borderRadius: '12px',
        gap: isMobile ? '16px' : '12px',
        opacity: isSubmitted ? 0.6 : 1
      }}
    >
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '12px' : '8px',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          justifyContent: isMobile ? 'space-between' : 'flex-start'
        }}>
          
          <input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            placeholder={RmConfigs?.RichmediaDOBDayPlaceholder || 'DD'}
            value={day}
            onChange={handleDayChange}
            onBlur={handleBlur}
            maxLength={2}
            disabled={isSubmitted}
            style={{
              width: isMobile ? '100px' : '50px',
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid #ccc',
              fontSize: '14px',
              textAlign: 'center',
              outline: 'none',
              flex: isMobile ? '1' : 'none'
            }}
          />
        </div>

        {!isMobile && <span style={{ fontSize: '14px', color: '#666' }}>/</span>}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          justifyContent: isMobile ? 'space-between' : 'flex-start'
        }}>
          
          <input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            placeholder={RmConfigs?.RichmediaDOBMonthPlaceholder || 'MM'}
            value={month}
            onChange={handleMonthChange}
            onBlur={handleBlur}
            maxLength={2}
            ref={monthRef}
            disabled={isSubmitted}
            style={{
              width: isMobile ? '100px' : '50px',
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid #ccc',
              fontSize: '14px',
              textAlign: 'center',
              outline: 'none',
              flex: isMobile ? '1' : 'none'
            }}
          />
        </div>

        {!isMobile && <span style={{ fontSize: '14px', color: '#666' }}>/</span>}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          justifyContent: isMobile ? 'space-between' : 'flex-start'
        }}>
          
          <input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            placeholder={RmConfigs?.RichmediaDOBYearPlaceholder || 'YYYY'}
            value={year}
            onChange={handleYearChange}
            onBlur={handleBlur}
            maxLength={4}
            ref={yearRef}
            disabled={isSubmitted}
            style={{
              width: isMobile ? '100px' : '80px',
              padding: '8px',
              borderRadius: '6px',
              border: '1px solid #ccc',
              fontSize: '14px',
              textAlign: 'center',
              outline: 'none',
              flex: isMobile ? '1' : 'none'
            }}
          />
        </div>
      </div>
      
      <button
        type="submit"
        disabled={isSubmitted || !!error || !day || !month || !year}
        style={{
          border: "none",
          borderRadius: "20px",
          padding: "10px 20px",
          width: "100%",
          height: "40px",
          backgroundColor: isSubmitted || !!error || !day || !month || !year ? "#cccccc" : "#af0000",
          color: isSubmitted || !!error || !day || !month || !year ? "#666666" : "#fff",
          fontSize: "14px",
          fontWeight: "bold",
          cursor: isSubmitted || !!error || !day || !month || !year ? "not-allowed" : "pointer",
          transition: "all 0.2s ease"
        }}
      >
        提交
      </button>
      
      {error && (
        <div style={{ 
          color: 'red', 
          fontSize: '12px', 
          marginTop: '4px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
    </form>
  );
};

export default DOBInput;
